/**
 * Calendar-modal helper: binary "all clients in this session have consented to
 * session recording" indicator. Mirrors the server-side Zoom auto-recording
 * gate (exact `Patient/{id}` refs only; fails closed on read errors).
 */
import type { MedplumClient } from '@medplum/core';
import { loadSessionRecordingDecision } from './sessionRecordingConsentClient';

const PATIENT_REF_RE = /^Patient\/([^/?#]+)$/;

/** Unique Patient ids for a session: the Case's client plus any `Patient/..` attendee refs. */
export function collectSessionRecordingPatientIds(params: {
  primaryPatientId?: string;
  attendeeRefs?: readonly string[];
}): string[] {
  const ids = new Set<string>();
  if (params.primaryPatientId) ids.add(params.primaryPatientId);
  for (const ref of params.attendeeRefs ?? []) {
    const match = PATIENT_REF_RE.exec(ref);
    if (match) ids.add(match[1]);
  }
  return [...ids];
}

/**
 * True only when every patient's current session-recording decision is `permitted`.
 * Returns false when any decision is pending/denied/revoked or a Consent read fails.
 */
export async function haveAllPatientsConsentedToRecording(
  medplum: MedplumClient,
  patientIds: readonly string[],
): Promise<boolean> {
  if (patientIds.length === 0) return false;
  try {
    const decisions = await Promise.all(
      patientIds.map((id) => loadSessionRecordingDecision(medplum, id)),
    );
    return decisions.every((d) => d === 'permitted');
  } catch {
    return false;
  }
}
